/**
 * Retrieval & Hybrid Search API
 * Metrics, A/B testing, and performance monitoring
 */

import { getAPIClient } from './client';

export interface RetrievalMetrics {
  total_queries: number;
  avg_latency_ms: number;
  avg_vector_count: number;
  avg_bm25_count: number;
  rerank_usage_percent: number;
}

export interface HybridSearchStats {
  stage1_retrieval: {
    vector_count: number;
    bm25_count: number;
    entity_count: number;
    latency_ms: number;
  };
  stage2_rrf: {
    fused_count: number;
    latency_ms: number;
    contribution: {
      total_rankings: number;
      ranking_sizes: number[];
      ranking_types: string[];
    };
  };
  stage3_rerank: {
    rerank_applied: boolean;
    reranked_count: number;
    latency_ms: number;
  };
  stage4_weighted: {
    latency_ms: number;
  };
  total_latency_ms: number;
}

export interface ABTestStats {
  variant_a_count: number;
  variant_b_count: number;
  variant_a_avg_latency_ms: number;
  variant_b_avg_latency_ms: number;
  variant_a_avg_results: number;
  variant_b_avg_results: number;
}

export interface BM25IndexStatus {
  project_id: string;
  index_exists: boolean;
  total_documents: number;
  index_size_mb: number;
  last_built_at?: string;
  build_status?: 'building' | 'complete' | 'error';
}

// Get aggregate retrieval metrics
export async function getRetrievalMetrics(
  projectId?: string,
  days: number = 7
): Promise<RetrievalMetrics> {
  const client = getAPIClient();
  return client.get<RetrievalMetrics>('/metrics/retrieval', {
    params: {
      project_id: projectId,
      days,
    },
  });
}

// Get BM25 index status
export async function getBM25IndexStatus(projectId: string): Promise<BM25IndexStatus> {
  const client = getAPIClient();
  return client.get<BM25IndexStatus>(`/indices/bm25/status/${projectId}`);
}

// Build BM25 index (trigger background job)
export async function buildBM25Index(projectId: string): Promise<{ job_id: string }> {
  const client = getAPIClient();
  return client.post<{ job_id: string }>(`/indices/bm25/build/${projectId}`);
}

// Get A/B test comparison stats
export async function getABTestStats(
  projectId?: string,
  days: number = 7
): Promise<ABTestStats> {
  const client = getAPIClient();
  return client.get<ABTestStats>('/metrics/ab-test', {
    params: {
      project_id: projectId,
      days,
    },
  });
}











